import { slug } from 'cuid'
import queryString from 'query-string'
import sha256 from 'crypto-js/sha256'
import base64 from 'crypto-js/enc-base64'
import decode from 'jwt-decode'
import { Request, Response } from 'express'
import { Logger } from 'typescript-log'
import { SectionMetaInfoDTO } from 'publication-types'
import { AuthenticationError } from './errors'

import { BaseClientConfig } from 'common/client/BaseClientConfig'
import { storeCookie, retrieveCookie, removeCookie } from 'common/data/cookies/cookies'
import { isServerEnvironment } from 'common/environment'
import { CommonServerConfig } from 'common/common-server-config'
import { LoginEvent, LoginRefreshEvent } from 'common/events/user-event-types'
import { LoginDetails } from 'common/data/authentication/reducer'
import { getBaseUrl } from 'common/routing/url'
import { utm_medium_online } from 'common/ssw'

export const authCallbackPath = '/auth/callback'
export const authPkceVerifierKey = 'authPkceVerifier'
export const authStateTokenKey = 'authStateToken'
export const authOriginUrlKey = 'authOriginUrl'
export const authTokensKey = 'authTokens'

const isProduction = process.env.NODE_ENV === 'production'

export interface AuthParms {
    redirect_uri: string
    client_id?: string
    state: string
    code_challenge: string
    code_challenge_method: 'S256'
    response_type: 'code'
}

export const getAuthCallbackURI = (publicUrl: string, sectionMeta?: SectionMetaInfoDTO) => {
    const url = getBaseUrl(publicUrl, sectionMeta)
    return `${url}${authCallbackPath}`
}

export function getLoginUrl(config: BaseClientConfig, sectionMeta?: SectionMetaInfoDTO) {
    if (isServerEnvironment()) {
        throw new Error('getLoginUrl should not be called during SSR')
    }

    const authSiteUrl = config.authTheWestUrl || 'https://auth.thewest.com.au'
    const params = getAuthParams(config, sectionMeta)

    return `${authSiteUrl}/login?${queryString.stringify(params)}`
}

export const getAuthParams = (
    config: BaseClientConfig,
    sectionMeta?: SectionMetaInfoDTO,
    stateGenerator?: () => string,
    codeChallengeGenerator?: () => string
): AuthParms => ({
    redirect_uri: getAuthCallbackURI(location.origin, sectionMeta),
    client_id: config.authClientId,
    state: stateGenerator ? stateGenerator() : getState(),
    code_challenge: codeChallengeGenerator ? codeChallengeGenerator() : getPkceChallenge(),
    code_challenge_method: 'S256',
    response_type: 'code',
})

interface SubscribeLinkOptions {
    config: BaseClientConfig
    pageUrl: string
    campaign?: string
    source?: string
    auth: Pick<AuthParms, 'redirect_uri' | 'state'>
    packagePath?: string
    medium?: string
    additionalParams?: { [key: string]: string | undefined }
}

export const getSubscribeUrl = ({
    config,
    pageUrl,
    auth,
    campaign = 'nav-subscription-button',
    source = 'thewest.com.au',
    packagePath = 'subscription-packages',
    medium = utm_medium_online,
    additionalParams,
}: SubscribeLinkOptions) => {
    const subscriberLink = config.subscribeTheWestUrl || 'https://subscriber.thewest.com.au'
    const { redirect_uri, state } = auth
    const params: { [key: string]: string | undefined } = {
        utm_source: source,
        utm_medium: medium,
        utm_campaign: campaign,
        redirect_origin: pageUrl,
        redirect_uri,
        state,
        ...additionalParams,
    }

    Object.keys(params).forEach(
        key => (params[key] === undefined || params[key] === '') && delete params[key]
    )

    return `${subscriberLink}/${packagePath}?${queryString.stringify(params)}`
}

/**
 * Creates, stores a PKCE Verifier token then returns a Code Challenge
 */
export const getPkceChallenge = () => {
    const pkceVerifier = generateRandomAuthString()

    storeCookie(
        authPkceVerifierKey,
        { pkceVerifier },
        {
            secure: isProduction,
        }
    )

    return generateCodeChallenge(pkceVerifier)
}
interface StateTokenCookie {
    stateToken: string
}

const stateTokenDefault = { stateToken: undefined }

/**
 * Creates, stores and returns a State token
 */
export const getState = () => {
    const hour = 60 * 60 * 1000
    const expiresInMs = 24 * hour
    const stateToken = generateRandomAuthString()
    storeCookie(
        authStateTokenKey,
        { stateToken },
        {
            secure: isProduction,
            maxAge: expiresInMs,
            sameSite: 'lax',
        }
    )

    return stateToken
}
/**
 * Generates a random string that fits within OAuth spec
 */
export const generateRandomAuthString = () =>
    Array.from(Array(7))
        .map(slug)
        .join('')

/**
 * Converts a PKCE Verifier token to a code challenge to be passed through the client request
 */
export const generateCodeChallenge = (codeVerifier: string) => sha256(codeVerifier).toString(base64)

/**
 * Matches stored stateToken against an incoming queryString stateToken, clears the token if successful
 */
export const verifyState = (stateFromQuery: string, request: Request) => {
    const { stateToken } =
        retrieveCookie<StateTokenCookie>(authStateTokenKey, request) || stateTokenDefault

    const stateMatch = stateToken && stateToken === stateFromQuery

    if (!stateMatch) {
        request.log.error({ stateToken, stateFromQuery }, 'State mismatch')
    }

    return stateMatch
}

/**
 * Persist Origin URL
 * This is the url that originates the login request
 */
export const persistOriginUrl = () => {
    if (isServerEnvironment()) {
        return
    }

    const originUrl = window.location.href
    storeCookie(authOriginUrlKey, { originUrl })
}

interface AuthCookieState {
    originUrl: string
}

export function retrieveOriginUrl(req: Request): string | undefined {
    const fallbackUrl = req.config.publicUrl

    const cookie = retrieveCookie<AuthCookieState>(authOriginUrlKey, req)

    if (!cookie) {
        req.log.error({ authOriginUrlKey, cookie }, 'Cookie parse error')
        return fallbackUrl
    }

    if (cookie && !cookie.originUrl) {
        req.log.error({ authOriginUrlKey, cookie }, 'originUrl not found in cookie')
        return fallbackUrl
    }

    return cookie.originUrl
}

export interface AuthTokenResponse {
    success: true
    access_token: string
    token_type: string
    expires_in: number
    refresh_token: string
}

export interface AuthTokenResponseError {
    success: false
    status: number
    error: {
        error: string
        errorDescription: string
    }
}

export interface AuthTokenPayload {
    iss: string
    apiKey: string
    iat: number
    exp: number
    sub: string
    'profile.firstName': string
    'profile.lastName': string
    'data.entitlements': string
    'data.occupantId': string
}

export interface AuthTokenState {
    expiryDate: number
    accessToken: string
    refreshToken: string
    tokenType: string
}

/**
 * Persist Tokens
 */
export const createAuthTokenState = (authResponse: AuthTokenResponse): AuthTokenState => {
    const expiryDate = new Date()
    expiryDate.setSeconds(expiryDate.getSeconds() + authResponse.expires_in)

    return {
        expiryDate: (expiryDate.getTime() / 1000) | 0,
        accessToken: authResponse.access_token,
        refreshToken: authResponse.refresh_token,
        tokenType: authResponse.token_type,
    }
}

export const persistAuthTokens = (authResponse: AuthTokenResponse, res?: Response) => {
    const tokenState = createAuthTokenState(authResponse)
    const expiresDays = 14

    if (res) {
        const hour = 60 * 60 * 1000
        const expiresInMs = expiresDays * 24 * hour
        res.cookie(authTokensKey, JSON.stringify(tokenState), {
            secure: isProduction,
            maxAge: expiresInMs,
            sameSite: 'lax',
        })
    } else {
        storeCookie(authTokensKey, tokenState, {
            secure: isProduction,
            expires: expiresDays,
            sameSite: 'lax',
        })
    }
}

export const retrieveAuthTokens = (req?: Request): AuthTokenState | undefined => {
    return retrieveCookie<AuthTokenState>(authTokensKey, req)
}

export const clearAuthTokens = (req?: Request): void => {
    return removeCookie(authTokensKey, req)
}

/**
 * Exchange auth site "code" for Authentication tokens
 */
export async function exchangeAuthsiteCodeForTokens(
    config: CommonServerConfig,
    log: Logger,
    code: string,
    sectionInfo: SectionMetaInfoDTO
): Promise<AuthTokenResponse> {
    try {
        const authTokenBody = {
            grant_type: 'authorization_code',
            client_id: config.authClientId,
            client_secret: config.authClientSecret,
            redirect_uri: getAuthCallbackURI(config.publicUrl, sectionInfo),
            code,
        }

        const response = await fetch(`${config.authTheWestUrl}/oauth/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                Caller: config.apiCallerHeader,
            },
            body: queryString.stringify(authTokenBody),
        })

        const responseBody = await response.json()

        if (response.status !== 200) throw new AuthenticationError(response, responseBody)

        return responseBody as AuthTokenResponse
    } catch (err) {
        log.debug({ err }, 'Error exchanging authorization code for an access token')
        throw err
    }
}

/**
 * Exchange auth site Refresh Token for new Authentication tokens
 */
export async function exchangeRefreshTokens(
    config: BaseClientConfig,
    log: Logger,
    refreshToken: string
): Promise<AuthTokenResponse> {
    try {
        const authTokenBody = {
            grant_type: 'refresh_token',
            client_id: config.authClientId,
            refresh_token: refreshToken,
        }
        const response = await fetch(`${config.authTheWestUrl}/oauth/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                Caller: config.apiCallerHeader,
            },
            body: queryString.stringify(authTokenBody),
        })

        const responseBody = await response.json()

        if (response.status !== 200) throw new AuthenticationError(response, responseBody)
        return responseBody as AuthTokenResponse
    } catch (err) {
        log.debug({ err }, 'Error Authenticating')
        throw err
    }
}

/**
 * Logout the current JWT token
 */
export async function authSiteLogout(
    config: BaseClientConfig,
    log: Logger
): Promise<boolean | undefined> {
    const authTokens = retrieveAuthTokens()

    if (!authTokens) {
        return
    }

    let response
    try {
        response = await fetch(`${config.authTheWestUrl}/logout`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${authTokens.accessToken}`,
                Caller: config.apiCallerHeader,
            },
        })

        return response.status === 200
    } catch (err) {
        log.debug({ response }, 'Error calling logout endpoint')
        log.error({ err }, 'Could not log out')
        return
    }
}

export function getAuthStateFromAccessToken(
    accessToken: string,
    onEvent: (loginEvent: LoginEvent | LoginRefreshEvent) => void
): LoginDetails & {
    onEvent: (loginEvent: LoginEvent | LoginRefreshEvent) => void
} {
    const payload = decode(accessToken) as AuthTokenPayload

    const firstName = payload['profile.firstName']
    const lastName = payload['profile.lastName']
    const entitlements = payload['data.entitlements']
    const occupantId = payload['data.occupantId']
    const gigyaId = payload.sub

    return {
        entitlements: entitlements ? entitlements.split('|') : [],
        gigyaId,
        occupantId,
        loginProvider: '',
        onEvent,
        socialProviders: '',
        userName: [firstName, lastName].filter(x => !!x).join(' '),
    }
}
